import React from "react";
import Seo from "../components/Seo";
import { filArianeJsonLd, serviceJsonLd, faqJsonLd } from "../utils/donneesStructurees";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CheckCircle, Phone, AlertTriangle, ArrowRight } from "lucide-react";
import { trackSimulatorClick, trackPhoneClick } from "../utils/analytics";
import { formaterEuros, PLAFOND_TRAVAUX, AIDE_MAX } from "../utils/plafonds";
import { listeDepartements } from "../data/departements";
import GuidesLies from "../components/GuidesLies";

/**
 * Page réécrite le 2026-08-06. Elle était en « Explorée, actuellement non
 * indexée » dans Search Console, et pour de bonnes raisons : 426 mots, un H1
 * (« AMO GRATUIT - ANAH PAIE TOUT ») sans rapport avec le title qui promettait
 * le cumul des aides, un contenu qui redisait le parcours en 4 étapes déjà
 * traité ailleurs, et un témoignage situé à Créteil, hors zone d'intervention.
 *
 * Conformité : l'accompagnement AMO est pris en charge par l'ANAH à hauteur de
 * 600 € (800 € dans certains cas), pas « tout payé par l'ANAH ». Les montants
 * d'aides locales viennent de data/departements.js avec leur source.
 */

const FAQ = [
  {
    question: "Toutes les aides sont-elles cumulables entre elles ?",
    answer:
      "Non, et c'est la nuance qui compte. MaPrimeAdapt' se cumule avec les aides de votre département, de votre commune, de votre caisse de retraite, ainsi qu'avec l'APA et la PCH. En revanche deux aides ne peuvent pas financer exactement la même dépense, et certaines aides locales s'excluent entre elles — c'est le cas en Essonne entre l'aide départementale et le complément de l'agglomération Paris-Saclay. On compare alors pour retenir la plus favorable.",
  },
  {
    question: "Le total des aides peut-il dépasser le coût des travaux ?",
    answer:
      "Jamais. Le cumul est plafonné au montant réellement dépensé : les aides se complètent, elles ne se superposent pas au-delà de la facture. Si le total théorique dépasse le coût des travaux, la dernière aide versée est réduite d'autant.",
  },
  {
    question: "Peut-on arriver à zéro reste à charge ?",
    answer:
      "Cela se voit, sur des projets modestes portés par un ménage très modeste, dans un département qui verse une aide locale et avec une caisse de retraite qui intervient. Mais ce n'est pas la règle, et personne ne peut vous le promettre avant d'avoir étudié votre situation. Méfiez-vous d'un interlocuteur qui l'annonce dès le premier appel.",
  },
  {
    question: "Le crédit d'impôt de 25 % entre-t-il encore dans le cumul ?",
    answer:
      "Non. Le crédit d'impôt autonomie est supprimé pour les dépenses payées à compter du 1er janvier 2026. Beaucoup de pages en ligne le mentionnent encore : elles n'ont pas été mises à jour. Il ne faut plus le compter dans votre plan de financement.",
  },
  {
    question: "Faut-il déposer les demandes dans un ordre particulier ?",
    answer:
      "Oui. MaPrimeAdapt' est l'aide principale et son instruction commande le calendrier, il faut donc la déposer en premier. Les demandes complémentaires partent en parallèle, avec des devis strictement identiques d'un dossier à l'autre — une différence de montant entre deux dossiers est un motif de rejet classique. Et dans tous les cas, aucun travail ne doit commencer avant l'accord de l'ANAH.",
  },
  {
    question: "La TVA réduite est-elle une aide à demander ?",
    answer:
      "Non, et c'est justement ce qui la rend facile à oublier. Le taux réduit de 5,5 % sur la fourniture et la pose des équipements d'adaptation est appliqué directement par l'artisan sur sa facture. Vous n'avez aucune démarche à faire, mais vous avez tout intérêt à vérifier qu'il l'a bien appliqué : entre 20 % et 5,5 %, l'écart sur un devis de 15 000 € dépasse 1 800 €.",
  },
];

const CumulAidesIDF = () => {
  const departements = listeDepartements();

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Cumuler les aides MaPrimeAdapt' en Île-de-France"
        description="Aides départementales, caisse de retraite, APA, PCH, TVA à 5,5 % : ce qui se cumule avec MaPrimeAdapt' en Île-de-France, et dans quel ordre les demander."
        chemin="/cumul-aides-ma-prime-adapt-idf"
        donnees={[
          serviceJsonLd({
            nom: "Optimisation du plan de financement MaPrimeAdapt'",
            description:
              "Recherche et montage des aides cumulables avec MaPrimeAdapt' en Île-de-France : aides départementales et communales, caisses de retraite, APA et PCH.",
            url: "/cumul-aides-ma-prime-adapt-idf",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "MaPrimeAdapt'", url: "/maprimeadapt" },
            { nom: "Cumul des aides en Île-de-France", url: "/cumul-aides-ma-prime-adapt-idf" },
          ]),
          faqJsonLd(FAQ),
        ]}
      />

      <Header />

      <main id="contenu">

      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Cumuler les aides pour adapter son logement en Île-de-France
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              MaPrimeAdapt' finance 70 % ou 50 % de vos travaux selon vos ressources. Ce
              qui fait la différence sur le reste à charge, ce sont les aides qui viennent
              s'y ajouter — et elles ne sont pas les mêmes d'un département francilien à
              l'autre. Cette page recense ce qui se cumule réellement, ce qui ne se cumule
              pas, et dans quel ordre déposer les demandes.
            </p>
            <Link
              to="/simulateur"
              onClick={() => trackSimulatorClick("cumul_hero")}
              className="inline-flex items-center gap-2 bg-orange-700 text-white text-lg font-bold px-8 py-4 rounded-lg hover:bg-orange-800 transition-colors"
            >
              Estimer mon reste à charge
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Le socle */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Le socle : MaPrimeAdapt'</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              C'est l'aide principale, et celle qui commande le calendrier de toutes les
              autres. Elle couvre 70 % du montant des travaux pour les ménages très
              modestes et 50 % pour les ménages modestes, dans la limite d'un plafond de
              travaux de {formaterEuros(PLAFOND_TRAVAUX)} HT — soit une aide qui peut
              atteindre {formaterEuros(AIDE_MAX.tresModestes)}.
            </p>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
              <div className="flex gap-3">
                <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={22} />
                <p className="text-gray-800 leading-relaxed">
                  Les {formaterEuros(PLAFOND_TRAVAUX)} sont un plafond de <em>travaux</em>,
                  pas un montant d'aide. C'est la dépense maximale prise en compte dans le
                  calcul, pas la somme que vous recevez.
                </p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">
              L'accompagnement par un assistant à maîtrise d'ouvrage est obligatoire pour
              obtenir l'aide. Son coût est pris en charge par l'ANAH à hauteur de 600 €,
              porté à 800 € pour les personnes de plus de 80 ans ou ayant subi une chute
              récente : vous avancez la somme, elle vous est remboursée avec les aides
              travaux.
            </p>
            <Link
              to="/plafonds-ressources-maprimeadapt"
              className="inline-block mt-4 text-orange-700 underline hover:text-orange-800"
            >
              Vérifier votre catégorie de ressources
            </Link>
          </div>
        </div>
      </section>

      {/* Ce qui s'ajoute */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Ce qui vient s'y ajouter</h2>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-orange-700 mb-3">
                  1. Les aides de votre département
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  C'est le poste qui varie le plus, et de loin. Les Hauts-de-Seine versent
                  une aide départementale à l'adaptation qui peut atteindre 4 200 € selon
                  les ressources ; la Seine-Saint-Denis finance 30 % du montant des travaux
                  pour les ressources très modestes via Rénov'Habitat 93 ; la Seine-et-Marne
                  prête à 2 % avec la CADAL 77 ; Paris n'a pas de subvention départementale
                  dédiée. Le même projet n'aboutit donc pas au même reste à charge d'un
                  département à l'autre.
                </p>
                <div className="flex flex-wrap gap-2">
                  {departements.map((d) => (
                    <Link
                      key={d.cle}
                      to={`/amo-anah-${d.cle}-${d.code}`}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:border-orange-500 hover:text-orange-700 transition-colors"
                    >
                      {d.nom} ({d.code})
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-orange-700 mb-3">
                  2. Votre caisse de retraite
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  C'est l'aide la plus souvent oubliée, et l'une des plus accessibles. En
                  Île-de-France, les retraités du régime général relèvent de la CNAV — et
                  non d'une CARSAT comme en région, ce qui égare beaucoup de monde dans les
                  recherches. S'y ajoutent l'Agirc-Arrco pour les retraites complémentaires,
                  la MSA pour le régime agricole et les caisses des fonctions publiques. Le
                  montant dépend de votre caisse et de votre situation : la demande se fait
                  directement auprès d'elle, en parallèle du dossier ANAH.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-orange-700 mb-3">
                  3. L'APA et la PCH, sur leur volet équipement
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  L'allocation personnalisée d'autonomie comporte un volet aides techniques,
                  et la prestation de compensation du handicap un volet aménagement du
                  logement. Ils se cumulent avec MaPrimeAdapt' à une condition : ne pas
                  financer exactement la même dépense. En pratique, on les mobilise sur ce
                  que l'ANAH ne prend pas — matériel mobile, siège de douche amovible,
                  monte-personne — pendant que MaPrimeAdapt' finance les travaux fixes.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-orange-700 mb-3">
                  4. Votre commune ou votre CCAS
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Les dispositifs communaux sont modestes mais réels, et très mal recensés :
                  leurs critères changent souvent et ils ne font l'objet d'aucune base
                  nationale. C'est une recherche à faire au cas par cas, au moment du montage
                  du dossier. À Paris, le centre d'action sociale de la Ville dispose d'une
                  section dans chaque arrondissement.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-orange-700 mb-3">
                  5. La TVA à 5,5 %, qui n'est pas une aide à demander
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  La fourniture et la pose des équipements d'adaptation — douche de
                  plain-pied, WC adapté, monte-escalier, barres d'appui, volets motorisés —
                  relèvent du taux réduit de 5,5 % au lieu de 20 %. L'artisan l'applique
                  directement sur sa facture, sans démarche de votre part. Vérifiez-le sur
                  chaque devis : sur 15 000 € de travaux, l'écart entre les deux taux dépasse
                  1 800 €.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Les deux règles */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Les deux règles qui limitent le cumul</h2>
            <div className="space-y-6">
              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Le total ne peut pas dépasser le coût des travaux
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Les aides se complètent, elles ne se superposent pas au-delà de la facture.
                  Si l'addition théorique dépasse la dépense réelle, la dernière aide versée
                  est réduite d'autant.
                </p>
              </div>
              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Deux aides ne financent pas la même dépense
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  C'est ce qui distingue un plan de financement solide d'un dossier rejeté.
                  Chaque poste de travaux doit être rattaché à une aide et une seule. Certains
                  dispositifs locaux s'excluent en outre entre eux : en Essonne, l'aide
                  départementale et le complément de l'agglomération Paris-Saclay ne se
                  cumulent pas l'un avec l'autre, même s'ils se cumulent chacun avec
                  MaPrimeAdapt'.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ordre */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Dans quel ordre s'y prendre</h2>
            <div className="space-y-4">
              {[
                [
                  "Déposer MaPrimeAdapt' en premier",
                  "C'est l'aide principale, et son instruction fixe le calendrier de tout le reste.",
                ],
                [
                  "Lancer les demandes complémentaires en parallèle",
                  "Département, caisse de retraite, commune. N'attendez pas la réponse de l'ANAH pour les envoyer : vous perdriez des mois.",
                ],
                [
                  "Utiliser des devis strictement identiques",
                  "Un montant qui diffère d'un dossier à l'autre est un motif de rejet classique, et il se découvre tard.",
                ],
                [
                  "Ne rien engager avant la notification",
                  "Aucun acompte, aucune livraison de matériel, aucune journée de chantier avant l'accord écrit de l'ANAH. C'est irrattrapable.",
                ],
              ].map(([titre, texte], i) => (
                <div key={titre} className="bg-white p-6 rounded-lg border border-gray-200 flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-700 text-white font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-bold text-gray-900">{titre}</h3>
                    <p className="text-gray-700 mt-1 leading-relaxed">{texte}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ce qui ne se cumule plus */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Ce qui ne se cumule plus</h2>
            <p className="text-gray-700 leading-relaxed">
              Besoin d'un accompagnement pour <Link to="/aides-complementaires" className="text-orange-700 underline hover:text-orange-800">rechercher et monter ces aides complémentaires</Link> ? C'est une prestation distincte de notre mission AMO. Le <strong>crédit d'impôt autonomie de 25 %</strong> est supprimé pour les
              dépenses payées à compter du 1<sup>er</sup> janvier 2026. Il figure encore sur
              beaucoup de pages et de simulateurs en ligne qui n'ont pas été mis à jour, et
              il fausse les plans de financement de plusieurs centaines d'euros. Ne le
              comptez plus.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-10">Questions fréquentes sur le cumul</h2>
            <div className="space-y-6">
              {FAQ.map((q) => (
                <div key={q.question} className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{q.question}</h3>
                  <p className="text-gray-700 leading-relaxed">{q.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ce que nous faisons */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Ce que nous faisons sur ce volet</h2>
            <ul className="space-y-3">
              {[
                "Nous identifions les aides mobilisables dans votre commune et votre département, pas seulement MaPrimeAdapt'.",
                "Nous rattachons chaque poste de travaux à une aide et une seule, pour éviter le rejet pour double financement.",
                "Nous vérifions que la TVA à 5,5 % est bien appliquée sur chaque devis.",
                "Nous déposons et suivons les dossiers complémentaires en parallèle du dossier ANAH.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle className="text-green-700 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-orange-600 to-orange-700 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Voyons ce que vous pouvez réellement cumuler
          </h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
            Le plan de financement dépend de votre commune, de votre caisse et de vos
            ressources. Un échange suffit à le dégrossir.
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <Link
              to="/simulateur"
              onClick={() => trackSimulatorClick("cumul_cta")}
              className="bg-white text-orange-700 text-xl font-bold px-10 py-4 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Faire la simulation
            </Link>
            <a
              href="tel:0939035444"
              onClick={() => trackPhoneClick("cumul_cta")}
              className="bg-transparent border-2 border-white text-white text-xl font-bold px-10 py-4 rounded-lg hover:bg-white hover:text-orange-700 transition-colors flex items-center gap-2"
            >
              <Phone size={24} />
              09 39 03 54 44
            </a>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["cumul-aides-maprimeadapt", "reste-a-charge-adaptation-logement"]} />

      </main>

      <Footer />
    </div>
  );
};

export default CumulAidesIDF;
